import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { debounce } from 'lodash';

import keyCodes from 'utils/keyCodes';
import { msg } from 'api';
import SearchSuggestions from '../containers/SearchSuggestions';
import MultiSearch from '../containers/MultiSearch';

export default class HeaderSearch extends PureComponent {
  static propTypes = {
    betaFeatures: PropTypes.array.isRequired,
    clear: PropTypes.func.isRequired,
    getSearchSuggestions: PropTypes.func.isRequired,
    multiSearchText: PropTypes.string,
    multiSearchLinkText: PropTypes.string,
    search: PropTypes.func.isRequired,
    searchSuggestionsList: PropTypes.array,
    searchSuggestionsTerm: PropTypes.string,
    setNextSearchSuggestionAsSelected: PropTypes.func.isRequired,
    setPreviousSearchSuggestionAsSelected: PropTypes.func.isRequired,
    setSearchSuggestionAsSelected: PropTypes.func.isRequired,
    showSimpleMultiSearch: PropTypes.bool,
    showMultiSearch: PropTypes.bool,
    update: PropTypes.func.isRequired,
    value: PropTypes.string,
  };

  static defaultProps = {
    searchSuggestionsTerm: '',
    searchSuggestionsList: [],
    showSimpleMultiSearch: false,
    value: '',
  };

  state = {
    isSuggestionsOpen: false,
    isInputActive: false,
    isInputFocused: false,
  };

  onSearchSuggestionMouseOver = (value) => {
    this.props.setSearchSuggestionAsSelected(value);
  };

  onSearchSuggestionClick = (value) => {
    this.props.search(value);
  };

  getSearchSuggestions(value) {
    const trimmedValue = value ? value.trim() : '';
    if (!trimmedValue.length) {
      return;
    }
    this.props.getSearchSuggestions(trimmedValue);
  }

  debouncedGetSearchSuggestions = debounce(this.getSearchSuggestions, 350);

  openSuggestions() {
    this.setState({ isSuggestionsOpen: true });

    document.addEventListener('click', this.collapseIfClickedOutside);
  }

  collapseSuggestions() {
    this.setState({ isSuggestionsOpen: false });

    document.removeEventListener('click', this.collapseIfClickedOutside);
  }

  handleClearSearch = () => {
    this.props.clear();
  };

  clearAndCollapse = () => {
    this.handleClearSearch();
    this.collapseSuggestions();
  };

  handleSearch = (event) => {
    event.preventDefault();
    if (this.props.value) {
      this.debouncedGetSearchSuggestions.cancel();
      this.props.search(this.props.value);
      this.searchInput.blur();
      this.clearAndCollapse();
    } else {
      this.searchInput.focus();
    }
  };

  collapseIfClickedOutside = (event) => {
    event.stopPropagation();
    const clickedInside = this.container && this.container.contains(event.target);
    if (!clickedInside) {
      this.collapseSuggestions();
    }
  };

  collapseIfFocusedOutside = () => {
    setTimeout(() => {
      const stillHasFocus = this.container && this.container.querySelector(':focus');
      if (!stillHasFocus) {
        this.collapseSuggestions();
      }
    }, 0);
  };

  handleChange = (event) => {
    const { value } = event.target;
    this.debouncedGetSearchSuggestions(value);
    this.props.update(value);
  };

  handleClick = (event) => {
    if (this.state.isInputActive && this.state.isInputFocused) {
      return;
    }
    this.handleFocus(event);
  }

  handleFocus = () => {
    this.setState({
      isInputActive: true,
      isInputFocused: true,
    });
    this.openSuggestions();
    this.props.getSearchSuggestions(this.props.value);
  };

  handleBlur = () => {
    this.setState({
      isInputActive: false,
      isInputFocused: false,
    });
  };

  handleMouseEnter = () => {
    this.setState({
      isInputActive: true,
    });
  };

  handleMouseLeave = () => {
    this.setState({
      isInputActive: false,
    });
  };

  handleKeyDown = (event) => {
    const { key, keyCode } = event;
    switch (key || keyCode) {
      case 'Escape':
      case keyCodes.ESCAPE:
        this.searchInput.blur();
        this.clearAndCollapse();
        break;
      case 'ArrowDown':
      case keyCodes.DOWN_ARROW:
        if (this.props.searchSuggestionsList.length) {
          this.props.setNextSearchSuggestionAsSelected();
        }
        break;
      case 'ArrowUp':
      case keyCodes.UP_ARROW:
        if (this.props.searchSuggestionsList.length) {
          this.props.setPreviousSearchSuggestionAsSelected();
        }
        break;
      default:
        // Do nothing, normal values are handled by onChange event
    }
  };

  render() { // eslint-disable-line max-lines-per-function
    const {
      multiSearchText,
      multiSearchLinkText,
      showSimpleMultiSearch,
      showMultiSearch,
      searchSuggestionsTerm,
      searchSuggestionsList,
    } = this.props;
    const { isInputActive, isInputFocused, isSuggestionsOpen } = this.state;
    const SearchSuggestionsInstance = (isSuggestionsOpen && (
      <SearchSuggestions
        clearAndCollapse={this.clearAndCollapse}
        isMoreProminentSearchEnabled
        multiSearchLinkText={multiSearchLinkText}
        onSearchSuggestionClick={this.onSearchSuggestionClick}
        onSearchSuggestionMouseOver={this.onSearchSuggestionMouseOver}
        searchSuggestionsTerm={searchSuggestionsTerm}
        searchSuggestionsList={searchSuggestionsList}
        showSimpleMultiSearch={showSimpleMultiSearch}
      />
    ));

    return (
      <div
        className={classNames(
          'hd-search__wrapper',
          'hd-searchProminent__wrapper',
          {
            'hd-search__wrapper--active': isSuggestionsOpen,
            'hd-searchProminent__wrapper--active': isSuggestionsOpen,
          }
        )}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <div
          className={classNames(
            'hd-search__singleSearch',
            { 'hd-searchProminent__singleSearch': showMultiSearch }
          )}
          onBlur={this.collapseIfFocusedOutside}
          ref={ref => { this.container = ref; }}
        >
          <form
            className={classNames(
              'hd-search__form',
              'hd-searchProminent__form',
              {
                'hd-searchProminent__form--active': isInputActive,
              }
            )}
            onSubmit={this.handleSearch}
          >
            <input
              id="search"
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              className={classNames(
                'hd-search__inputField',
                'hd-searchProminent__inputField',
                {
                  'hd-searchProminent__inputField--withMultiSearch': showMultiSearch,
                }
              )}
              maxLength="50"
              name="entry"
              onChange={this.handleChange}
              onClick={this.handleClick}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              onKeyDown={this.handleKeyDown}
              placeholder={msg.search.placeholder}
              ref={(input) => { this.searchInput = input; }}
              title={msg.search.placeholder}
              type="text"
              value={this.props.value}
              aria-autocomplete="list"
              aria-owns={isSuggestionsOpen ? 'hd-searchSuggestions' : null}
              aria-haspopup={isSuggestionsOpen}
            />
            <button
              type="button"
              className={classNames(
                'hd-search__clear',
                'hd-searchProminent__clear',
              )}
              onClick={this.handleClearSearch}
              aria-label="Clear"
            />
            <span className="hd-search__submit">
              <input
                type="submit"
                className={classNames(
                  'hd-search__submitButton',
                  'hd-searchProminent__submitButton',
                  {
                    'hd-searchProminent__submitButton--active': isInputFocused,
                  }
                )}
                aria-label="Search"
                value={msg.search.submitBtn}
              />
            </span>
            <button
              type="button"
              className={classNames(
                'hd-search__cancel',
                'hd-searchProminent__cancel',
              )}
              onClick={this.clearAndCollapse}
            >
              Cancel
            </button>
            {SearchSuggestionsInstance}
          </form>
        </div>
        {showMultiSearch && <MultiSearch buttonText={multiSearchText} />}
      </div>
    );
  }
}
